import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Flag, RotateCcw, X, XCircle } from "lucide-react";
import { api, idr } from "../../../lib/api";
import { useAuth } from "../../../context/AuthContext";
import { formatTanggal } from "../../../lib/tanggal";
import { pengajuSama } from "../validasiPiutang";
import RingkasanSelesai from "./RingkasanSelesai";

// Program Price Protection SELESAI oleh PIC (brief owner 22 Sep 2026 A9, PP4).
// Semua angka & keputusan "boleh apa" datang dari backend
// (`GET /cashback/programs/{id}/status-selesai`) — layar tidak menghitung total
// sendiri dan tidak menilai sendiri siapa PIC-nya (tombol yang berujung 403,
// aturan #3). Gerbang `can()` memakai kunci yang SAMA dengan endpointnya:
// ajukan/batal = finance_cashback_claim.update, putuskan = pp_pic_selesai.update,
// buka kembali = pp_pic_selesai.delete. `pp_pic_selesai` hanya lewat centang
// eksplisit (bawaan admin pun tolak — MODUL_EKSPLISIT_SAJA di AuthContext).
export default function PanelSelesai({ program, cbBase, onBerubah }) {
  const { can, user } = useAuth();
  const bolehKelola = can("finance_cashback_claim", "update");
  const bolehPutus = can("pp_pic_selesai", "update");
  const bolehBuka = can("pp_pic_selesai", "delete");
  const [status, setStatus] = useState(null);
  const [galat, setGalat] = useState(null);
  const [modal, setModal] = useState(null);
  const [muat, setMuat] = useState(0);

  useEffect(() => {
    let batal = false;
    setGalat(null);
    api.get(`${cbBase}/programs/${program.id}/status-selesai`)
      .then((r) => { if (!batal) setStatus(r.data); })
      .catch((e) => { if (!batal) setGalat(e?.response?.data?.detail || "Gagal memuat status Selesai"); });
    return () => { batal = true; };
  }, [cbBase, program.id, muat]);

  const selesaiAksi = () => { setModal(null); setMuat((n) => n + 1); onBerubah?.(); };
  const pj = status?.pengajuan;

  if (galat) return <p className="text-sm text-rose-700" data-testid="cb-selesai-galat">{galat}</p>;
  if (!status) return <p className="text-sm text-slate-500">Memuat status Selesai…</p>;

  return (
    <div className="space-y-3" data-testid="cb-selesai-panel">
      <RingkasanSelesai status={status} />
      {pj && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900" data-testid="cb-selesai-pengajuan-info">
          <p className="font-semibold flex items-center gap-1.5"><Flag className="w-4 h-4" /> Menunggu keputusan Selesai dari PIC {pj.pic_nama}</p>
          <p className="text-xs mt-1">Diajukan {pj.oleh_nama} · {formatTanggal(pj.at, { denganJam: true })}{pj.catatan ? ` — ${pj.catatan}` : ""}</p>
          <p className="text-xs mt-1">Selama menunggu, program ini terkunci: tidak bisa diedit, diimpor, dipindah, atau ditambah klaim baru. Batch yang sudah ada tetap bisa diproses.</p>
        </div>
      )}
      {(status.alasan_belum_bisa_disetujui || []).length > 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-700 space-y-0.5" data-testid="cb-selesai-syarat">
          <p className="font-semibold text-slate-800">Belum bisa disetujui Selesai karena:</p>
          {status.alasan_belum_bisa_disetujui.map((a, i) => <p key={i}>• {a}</p>)}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {bolehKelola && status.boleh_ajukan && (
          <button onClick={() => setModal("ajukan")} data-testid="cb-prog-act-ajukan-selesai"
                  className="h-9 px-3 rounded-lg border border-emerald-600 bg-emerald-600 text-white text-sm font-medium inline-flex items-center gap-1.5 hover:bg-emerald-700">
            <Flag className="w-4 h-4" /> Ajukan Selesai
          </button>
        )}
        {bolehPutus && status.boleh_putuskan && !pengajuSama(pj, user) && (
          <button onClick={() => setModal("putuskan")} data-testid="cb-prog-act-putuskan-selesai"
                  className="h-9 px-3 rounded-lg border border-emerald-600 bg-emerald-600 text-white text-sm font-medium inline-flex items-center gap-1.5 hover:bg-emerald-700">
            <CheckCircle2 className="w-4 h-4" /> Putuskan Selesai
          </button>
        )}
        {bolehKelola && status.boleh_batal && (
          <button onClick={() => setModal("batal")} data-testid="cb-prog-act-batal-ajukan-selesai"
                  className="h-9 px-3 rounded-lg border border-slate-200 text-slate-700 text-sm font-medium inline-flex items-center gap-1.5 hover:bg-slate-50">
            <XCircle className="w-4 h-4" /> Batalkan Pengajuan
          </button>
        )}
        {bolehBuka && status.boleh_buka_kembali && (
          <button onClick={() => setModal("buka")} data-testid="cb-prog-act-buka-kembali"
                  className="h-9 px-3 rounded-lg border border-rose-200 text-rose-700 text-sm font-medium inline-flex items-center gap-1.5 hover:bg-rose-50">
            <RotateCcw className="w-4 h-4" /> Buka Kembali
          </button>
        )}
      </div>
      {modal === "ajukan" && (
        <AjukanSelesaiModal program={program} status={status} cbBase={cbBase} onClose={() => setModal(null)} onSelesai={selesaiAksi} />
      )}
      {modal === "putuskan" && (
        <PutuskanSelesaiModal program={program} status={status} cbBase={cbBase} onClose={() => setModal(null)} onSelesai={selesaiAksi}
                              onMuatUlang={() => { setModal(null); setMuat((n) => n + 1); }} />
      )}
      {modal === "batal" && (
        <AlasanModal judul="Batalkan Pengajuan Selesai" label="Alasan pembatalan" tombol="Batalkan Pengajuan" testid="cb-selesai-batal"
                     onClose={() => setModal(null)}
                     onKirim={(alasan) => api.post(`${cbBase}/programs/${program.id}/batal-ajukan-selesai`, { pengajuan_id: pj.id, alasan })}
                     onSelesai={selesaiAksi} />
      )}
      {modal === "buka" && (
        <AlasanModal judul="Buka Kembali Program Selesai" label="Alasan membuka kembali" tombol="Buka Kembali" testid="cb-selesai-buka"
                     peringatan="Membuka kembali membuat angka program dihitung ulang dari data sekarang. Snapshot lama tetap tersimpan di riwayat."
                     onClose={() => setModal(null)}
                     onKirim={(alasan) => api.post(`${cbBase}/programs/${program.id}/buka-kembali`, { alasan })}
                     onSelesai={selesaiAksi} />
      )}
    </div>
  );
}

function Bingkai({ judul, testid, onClose, children, kaki }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid={testid}
           className="bg-white rounded-2xl w-full max-w-lg max-h-[88vh] flex flex-col shadow-2xl">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">{judul}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-3 text-sm">{children}</div>
        <div className="p-4 border-t border-slate-100 flex items-center justify-end gap-2">{kaki}</div>
      </div>
    </div>
  );
}

function AjukanSelesaiModal({ program, status, cbBase, onClose, onSelesai }) {
  const [calon, setCalon] = useState(null);
  const [pic, setPic] = useState("");
  const [catatan, setCatatan] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`${cbBase}/pp/pic-selesai`).then((r) => setCalon(r.data.items || []))
      .catch((e) => { setCalon([]); toast.error(e?.response?.data?.detail || "Gagal memuat daftar PIC"); });
  }, [cbBase]);
  const simpan = async () => {
    if (!pic) return;
    setBusy(true);
    try {
      const r = await api.post(`${cbBase}/programs/${program.id}/ajukan-selesai`, { pic_email: pic, catatan: catatan.trim() });
      toast.success(r.data.message);
      onSelesai();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengajukan Selesai"); }
    finally { setBusy(false); }
  };
  return (
    <Bingkai judul={`Ajukan Selesai · ${program.code}`} testid="cb-selesai-ajukan-modal" onClose={onClose}
             kaki={<>
               <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
               <button onClick={simpan} disabled={busy || !pic} data-testid="cb-selesai-ajukan-simpan"
                       className="h-10 px-4 rounded-lg bg-emerald-600 text-white text-sm font-medium disabled:opacity-50">{busy ? "Memproses..." : "Ajukan ke PIC"}</button>
             </>}>
      <p className="text-slate-600">Program akan terkunci sampai PIC memutuskan. Total yang dihitung saat ini:</p>
      <RingkasanSelesai status={status} />
      {(status.alasan_belum_bisa_disetujui || []).length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-xs text-amber-900 space-y-0.5" data-testid="cb-selesai-ajukan-peringatan">
          <p className="font-medium">PIC belum akan bisa menyetujui selama:</p>
          {status.alasan_belum_bisa_disetujui.map((a, i) => <p key={i}>• {a}</p>)}
        </div>
      )}
      <label className="block">
        <span className="text-xs font-medium text-slate-600">PIC penyetuju</span>
        <select value={pic} onChange={(e) => setPic(e.target.value)} data-testid="cb-selesai-pic"
                className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-2">
          <option value="">— pilih PIC —</option>
          {(calon || []).map((c) => <option key={c.id} value={c.email}>{c.nama}</option>)}
        </select>
        {calon && calon.length === 0 && (
          <span className="text-[11px] text-rose-700" data-testid="cb-selesai-pic-kosong">Belum ada akun yang dicentang izin "PIC Penyetuju Program Selesai" (selain Anda) — minta admin mencentangnya di Atur Izin.</span>
        )}
      </label>
      <label className="block">
        <span className="text-xs font-medium text-slate-600">Catatan (opsional)</span>
        <textarea rows={2} value={catatan} onChange={(e) => setCatatan(e.target.value)} data-testid="cb-selesai-ajukan-catatan"
                  className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5" />
      </label>
    </Bingkai>
  );
}

function PutuskanSelesaiModal({ program, status, cbBase, onClose, onSelesai, onMuatUlang }) {
  const pj = status.pengajuan;
  const r = status.ringkasan || {};
  const syarat = status.syarat || {};
  const adaHakBelum = ((syarat.hak_belum_diklaim || {}).count || 0) > 0;
  // `settlement_bermasalah` (tinjauan PP r2): entri "Perlu pemeriksaan", void
  // belum tuntas, atau Credit Note mati — backend menolak Setujui (409) selama
  // ada, jadi pilihan Setujui di sini ikut tertahan.
  const tertahan = (syarat.batch_belum_final || []).length > 0 || (syarat.item_belum_tuntas || 0) > 0
    || (syarat.settlement_bermasalah || []).length > 0;
  const [keputusan, setKeputusan] = useState("");
  const [catatan, setCatatan] = useState("");
  const [lepaskan, setLepaskan] = useState(false);
  const [busy, setBusy] = useState(false);
  const bisaSetujui = !tertahan && (!adaHakBelum || lepaskan);
  const valid = keputusan === "tolak" ? catatan.trim().length > 0 : keputusan === "setujui" && bisaSetujui;
  const simpan = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      const res = await api.post(`${cbBase}/programs/${program.id}/putuskan-selesai`, {
        pengajuan_id: pj.id, keputusan, catatan: catatan.trim(), lepaskan_hak_belum_diklaim: lepaskan,
        // Angka yang DILIHAT — backend menolak (409) kalau hitungan ulangnya berbeda.
        // `sidik` dari backend memuat unit yang haknya akan dilepas (tinjauan PP
        // r1: void + batal batch di sela modal ini dulu melepas hak lebih besar
        // dari yang dilihat PIC dengan jumlah & total yang sama).
        ringkasan_dilihat: { jumlah_baris: r.jumlah_baris, total_hak: (r.total_hak || {}).total_cashback,
                             sidik: status.sidik_putusan },
      });
      toast.success(res.data.message);
      onSelesai();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan keputusan");
      // 409 = angka/pengajuan berubah sejak modal dibuka (sidik putusan). Ringkasan
      // dimuat ulang otomatis supaya PIC memutuskan atas angka TERBARU — modal
      // basi yang tetap terbuka hanya akan ditolak lagi.
      if (e?.response?.status === 409) onMuatUlang?.();
    }
    finally { setBusy(false); }
  };
  return (
    <Bingkai judul={`Putuskan Selesai · ${program.code}`} testid="cb-selesai-putuskan-modal" onClose={onClose}
             kaki={<>
               <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
               <button onClick={simpan} disabled={busy || !valid} data-testid="cb-selesai-putuskan-simpan"
                       className="h-10 px-4 rounded-lg bg-emerald-600 text-white text-sm font-medium disabled:opacity-50">{busy ? "Memproses..." : "Simpan Keputusan"}</button>
             </>}>
      <p className="text-slate-600">Diajukan {pj.oleh_nama}{pj.catatan ? ` — "${pj.catatan}"` : ""}.</p>
      <RingkasanSelesai status={status} />
      {tertahan && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-2.5 text-xs text-rose-800 space-y-0.5" data-testid="cb-selesai-putuskan-tertahan">
          {(status.alasan_belum_bisa_disetujui || []).map((a, i) => <p key={i}>• {a}</p>)}
        </div>
      )}
      <div className="flex gap-2">
        <button onClick={() => setKeputusan("setujui")} data-testid="cb-selesai-keputusan-setujui"
                className={`flex-1 h-10 rounded-lg border text-sm font-medium ${keputusan === "setujui" ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-200 text-slate-700"}`}>Setujui Selesai</button>
        <button onClick={() => setKeputusan("tolak")} data-testid="cb-selesai-keputusan-tolak"
                className={`flex-1 h-10 rounded-lg border text-sm font-medium ${keputusan === "tolak" ? "bg-rose-600 border-rose-600 text-white" : "border-slate-200 text-slate-700"}`}>Tolak</button>
      </div>
      {keputusan === "setujui" && adaHakBelum && (
        <label className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-xs text-amber-900">
          <input type="checkbox" checked={lepaskan} onChange={(e) => setLepaskan(e.target.checked)} data-testid="cb-selesai-lepaskan-hak" className="mt-0.5" />
          <span>Lepaskan hak yang belum diklaim: {(syarat.hak_belum_diklaim || {}).count} unit ({idr((syarat.hak_belum_diklaim || {}).total_cashback || 0)}) dicatat HANGUS dan ikut dihitung di recovery rate dashboard.</span>
        </label>
      )}
      <label className="block">
        <span className="text-xs font-medium text-slate-600">{keputusan === "tolak" ? "Alasan penolakan (wajib)" : "Catatan (opsional)"}</span>
        <textarea rows={2} value={catatan} onChange={(e) => setCatatan(e.target.value)} data-testid="cb-selesai-putuskan-catatan"
                  className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5" />
      </label>
    </Bingkai>
  );
}

function AlasanModal({ judul, label, tombol, testid, peringatan, onClose, onKirim, onSelesai }) {
  const [alasan, setAlasan] = useState("");
  const [busy, setBusy] = useState(false);
  const simpan = async () => {
    if (!alasan.trim()) return;
    setBusy(true);
    try {
      const r = await onKirim(alasan.trim());
      toast.success(r.data.message);
      onSelesai();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memproses"); }
    finally { setBusy(false); }
  };
  return (
    <Bingkai judul={judul} testid={`${testid}-modal`} onClose={onClose}
             kaki={<>
               <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
               <button onClick={simpan} disabled={busy || !alasan.trim()} data-testid={`${testid}-simpan`}
                       className="h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium disabled:opacity-50">{busy ? "Memproses..." : tombol}</button>
             </>}>
      {peringatan && <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5">{peringatan}</p>}
      <label className="block">
        <span className="text-xs font-medium text-slate-600">{label}</span>
        <textarea rows={3} value={alasan} onChange={(e) => setAlasan(e.target.value)} data-testid={`${testid}-alasan`}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5" />
      </label>
    </Bingkai>
  );
}
